import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Container, Row, Col, Button, Alert, Spinner } from "react-bootstrap";

import api from "../../services/api";
import Template from "../../template";
import { useAuth } from "../../context/auth";
import CarregarPDFUERJ from "../../components/CarregarPDFUERJ";

import * as S from "../CorrecaoUERJ/styled";

function VisualizarCorrecaoUERJ(props) {
  const { id } = props.match.params;
  const history = useHistory();
  const { user } = useAuth();

  // Estados para dados da redação
  const [redacao, setRedacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Estados para marcações visuais (somente leitura)
  const [marcacoes, setMarcacoes] = useState([]);

  // Responsividade: desktop vs mobile
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 992 : true);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const panelHeight = isDesktop ? 'calc(100vh - 200px)' : 'calc(100vh - 120px)';
  const panelOverflowY = 'auto';
  // Limite máximo de altura para regiões roláveis (crescem dinamicamente)
  const sectionMaxHeight = '820px';

  // Mapa de cores por competência (mesmo padrão do PDF)
  const competenciaColors = {
    adequacao_tema: "#ff6b6b",
    tipo_texto: "#4ecdc4",
    argumentacao: "#45b7d1",
    periodo_coesao: "#96ceb4",
    modalidade: "#feca57"
  };

  const hexToRgba = (hex, alpha = 0.15) => {
    const cleaned = hex.replace('#', '');
    const bigint = parseInt(cleaned, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const getCompetenciaColor = (comp) => competenciaColors[comp] || "#6c757d";

  // Suporte a códigos de competência e números
  const CODE_TO_COMPETENCIA = {
    a: "adequacao_tema",
    t: "tipo_texto",
    r: "argumentacao",
    p: "periodo_coesao",
    m: "modalidade"
  };

  const NUMBER_TO_COMPETENCIA = {
    "1": "modalidade",
    "2": "tipo_texto",
    "3": "argumentacao",
    "4": "periodo_coesao",
    "5": "adequacao_tema"
  };

  const normalizeCompetencia = (comp) => {
    if (comp === undefined || comp === null) return null;
    const val = String(comp).toLowerCase().trim();
    if (competenciaColors[val]) return val;
    if (CODE_TO_COMPETENCIA[val]) return CODE_TO_COMPETENCIA[val];
    if (NUMBER_TO_COMPETENCIA[val]) return NUMBER_TO_COMPETENCIA[val];
    return val;
  };

  const resolveCompetencia = (marcacao) => {
    const raw = marcacao?.competencia ?? marcacao?.content?.competencia ?? marcacao?.conteudo?.competencia ?? marcacao?.comment?.competencia;
    return normalizeCompetencia(raw);
  };

  const competenciaLabels = {
    adequacao_tema: 'Adequação ao Tema',
    tipo_texto: 'Tipo de Texto',
    argumentacao: 'Argumentação',
    periodo_coesao: 'Estruturação do Período e Coesão',
    modalidade: 'Modalidade'
  };

  const getCompetenciaLabel = (compKey) => competenciaLabels[compKey] || 'Competência';

  // Mapeamento de subcompetências com textos exatamente iguais ao quadro UERJ
  const SUBCOMPETENCIAS = {
    adequacao_tema: [
      { code: 11, label: 'Não trata o tema' },
      { code: 12, label: 'Tangencia o tema' },
      { code: 13, label: 'Trata o tema de forma previsível' },
      { code: 14, label: 'Possui marcas pontuais de autoria e argumentos previsíveis' },
      { code: 15, label: 'Autoria e Defesa Consistente de Ponto de Vista' },
    ],
    tipo_texto: [
      { code: 21, label: 'Não é de modo algum uma dissertação. Não é texto em prosa e/ou não defende uma opinião.' },
      { code: 22, label: 'É antes uma dissertação ou narração com difuso propósito dissertativo.' },
      { code: 23, label: 'Enumera fatos ou ideias sem feição dissertativa.' },
      { code: 24, label: 'Atende à estrutura da dissertação, MAS DE MANEIRA ESQUEMÁTICA, COMO SE PRESO A UM MODELO PRÉVIO.' },
      { code: 25, label: 'Atende à estrutura da dissertação de maneira claramente pessoal e consistente.' },
    ],
    argumentacao: [
      { code: 31, label: 'Não desenvolve a argumentação' },
      { code: 32, label: 'Argumentos com pouca pertinência ou suficiência' },
      { code: 33, label: 'Argumenta, ainda que de modo pertinente, mas sem projeto claro de texto.' },
      { code: 34, label: 'Apresenta argumentos pertinentes, mas com incoerências localizadas ou com pouca eficácia.' },
      { code: 35, label: 'Argumenta de maneira pertinente, suficiente e coerente.' },
    ],
    periodo_coesao: [
      { code: 41, label: 'Estruturação caótica (impede a clareza)' },
      { code: 42, label: 'Problemas frequentes de estruturação sintática e/ou de uso precário de elementos coesivos (afetam clareza)' },
      { code: 43, label: 'Problemas localizados de estruturação de período e/ou de coesão' },
      { code: 44, label: 'Uso adequado de recursos coesivos e estruturação sintática sem comprometimento' },
      { code: 45, label: 'Uso produtivo de recursos coesivos e de recursos sintáticos (ênfase, delimitação, paralelismo, etc.)' },
    ],
    modalidade: [
      { code: 51, label: 'Problemas graves e generalizados de modalidade' },
      { code: 52, label: 'Problemas de modalidade pouco aceitáveis para o nível de escolaridade' },
      { code: 53, label: 'Problemas localizados de modalidade e/ou marcas de oralidade' },
      { code: 54, label: 'Bom domínio da variedade padrão da língua' },
      { code: 55, label: 'Excelente domínio da variedade da língua' },
    ],
  };
  const getSelectedSubcompetencias = (compKey) => {
    const arr = redacao?.[`sub_${compKey}`] || [];
    const items = SUBCOMPETENCIAS[compKey] || [];
    return items.filter(it => arr.includes(it.code));
  };

  // Buscar dados da redação específica
  useEffect(() => {
    async function buscarRedacao() {
      try {
        setLoading(true);
        console.log("🔍 Buscando redação UERJ com ID:", id);
        const response = await api.get(`/redacao/uerj/${id}`);
        console.log("📊 Resposta da API redação UERJ:", response.data);
        
        if (response.data.redacao) {
          const redacaoData = response.data.redacao;
          console.log("✅ Dados da redação carregados:", redacaoData);
          setRedacao(redacaoData);
        } else {
          console.log("❌ Nenhuma redação encontrada na resposta");
        }
      } catch (err) {
        console.error("❌ Erro ao buscar redação:", err);
        setError("Erro ao carregar a redação. Verifique se você tem permissão para acessá-la.");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      buscarRedacao();
    }
  }, [id]);

  // Buscar marcações existentes (somente leitura)
  useEffect(() => {
    async function buscarMarcacoes() {
      try {
        const response = await api.get(`/marcacao/${id}`);
        if (response.data.data) {
          const marcacoesSalvas = response.data.data.map(marcacao => {
            // Parse do conteúdo se for string JSON
            let parsedContent = marcacao.conteudo;
            if (typeof marcacao.conteudo === 'string') {
              try {
                parsedContent = JSON.parse(marcacao.conteudo);
              } catch (e) {
                console.error('Erro ao fazer parse do conteúdo:', e);
                parsedContent = marcacao.conteudo;
              }
            }
            
            return {
              ...marcacao,
              content: parsedContent,
              saved: true
            };
          });
          console.log('Marcações carregadas:', marcacoesSalvas);
          setMarcacoes(marcacoesSalvas);
        }
      } catch (err) {
        console.error("Erro ao buscar marcações:", err);
      }
    }

    if (id) {
      buscarMarcacoes();
    }
  }, [id]);

  if (loading) {
    return (
      <Template>
        <Container>
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando redação...</p>
          </div>
        </Container>
      </Template>
    );
  }

  if (error) {
    return (
      <Template>
        <Container>
          <Alert variant="danger" className="mt-4">
            <Alert.Heading>Erro ao carregar redação</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={() => history.goBack()}>
              Voltar
            </Button>
          </Alert>
        </Container>
      </Template>
    );
  }

  if (!redacao) {
    return (
      <Template>
        <Container>
          <Alert variant="warning" className="mt-4">
            <Alert.Heading>Redação não encontrada</Alert.Heading>
            <p>A redação solicitada não foi encontrada.</p>
            <Button variant="outline-warning" onClick={() => history.goBack()}>
              Voltar
            </Button>
          </Alert>
        </Container>
      </Template>
    );
  }

  return (
    <Template>
      <S.MainWrapper style={{ overflowY: isDesktop ? 'hidden' : 'auto', height: isDesktop ? 'calc(100vh - 60px)' : 'auto' }}>
        <Container fluid>
          <Row style={{ height: isDesktop ? '100%' : 'auto' }}>
            <Col sm={12} style={{ height: isDesktop ? '100%' : 'auto' }}>
              <S.MainContent style={{ height: isDesktop ? '100%' : 'auto' }}>
                {/* Cabeçalho */}
                <Row className="mb-4">
                  <Col>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h2 className="mb-1">
                          <i className="fas fa-eye text-primary mr-2"></i>
                          Minha Correção UERJ
                        </h2>
                        {user.nivel !== 'e' && (
                          <p className="text-muted mb-0">
                            Tema: <strong>{redacao.tema_nome || `ID: ${redacao.tema_id}`}</strong> | 
                            Classe: <strong>{redacao.classe_nome || `ID: ${redacao.classe_id}`}</strong>
                          </p>
                        )}
                        <small className="text-muted">
                          Enviada em: {redacao.enviada_date ? new Date(redacao.enviada_date).toLocaleDateString('pt-BR') : 'Data não disponível'}
                        </small>
                      </div>
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => history.goBack()}
                      >
                        <i className="fas fa-arrow-left mr-1"></i>
                        Voltar
                      </Button>
                    </div>
                  </Col>
                </Row>

                <Row>
                  {/* Visualização da Redação */}
                  <Col lg={8}>
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-0">
                        {redacao.url ? (
                          <div style={{ height: panelHeight }}>
                            <CarregarPDFUERJ
                              redacaoId={id}
                              pdfUrl={`https://redacoes.nyc3.digitaloceanspaces.com/${redacao.url}`}
                              readOnly={true}
                              marcacoesExistentes={marcacoes}
                            />
                          </div>
                        ) : (
                          <div className="text-center p-5">
                            <i className="fas fa-file-pdf text-muted" style={{fontSize: '3rem'}}></i>
                            <p className="mt-3 text-muted">Arquivo da redação não disponível</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Painel de Notas e Marcações (Somente Leitura) */}
                  <Col lg={4}>
                    <div className={`card border-0 shadow-sm ${isDesktop ? 'sticky-top' : ''}`} style={{ top: isDesktop ? '20px' : '0', height: panelHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <div className="card-header bg-success text-white">
                        <h5 className="mb-0">
                          <i className="fas fa-clipboard-check mr-2"></i>
                          Sua Nota UERJ
                        </h5>
                      </div>
                      <div className="card-body d-flex flex-column" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                        {/* Notas das Competências (Somente Leitura) */}
                        <div className="mb-4" style={{ maxHeight: sectionMaxHeight, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', flex: '0 0 auto' }}>
                          <h6 className="mb-3">
                            <i className="fas fa-star text-warning mr-2"></i>
                            Competências Avaliadas
                          </h6>
                          
                          {/* Adequação ao Tema */}
                          <div className="mb-3 p-3 bg-light rounded">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="font-weight-bold">
                                <i className="fas fa-bullseye text-primary mr-1"></i>
                                Adequação ao Tema
                              </span>
                              <span className="badge badge-pill" style={{ backgroundColor: competenciaColors['adequacao_tema'], color: '#fff' }}>
                                {redacao.adequacao_tema || 0}/4
                              </span>
                            </div>
                            {getSelectedSubcompetencias('adequacao_tema').length > 0 && (
                              <div className="mt-2">
                                {getSelectedSubcompetencias('adequacao_tema').map(item => (
                                  <div key={item.code} className="d-flex align-items-start mb-1">
                                    <i className="fas fa-check-circle text-success mr-2" aria-hidden="true"></i>
                                    <small>{item.label}</small>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Tipo de Texto */}
                          <div className="mb-3 p-3 bg-light rounded">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="font-weight-bold">
                                <i className="fas fa-file-alt text-success mr-1"></i>
                                Tipo de Texto
                              </span>
                              <span className="badge badge-pill" style={{ backgroundColor: competenciaColors['tipo_texto'], color: '#fff' }}>
                                {redacao.tipo_texto || 0}/4
                              </span>
                            </div>
                            {getSelectedSubcompetencias('tipo_texto').length > 0 && (
                              <div className="mt-2">
                                {getSelectedSubcompetencias('tipo_texto').map(item => (
                                  <div key={item.code} className="d-flex align-items-start mb-1">
                                    <i className="fas fa-check-circle text-success mr-2" aria-hidden="true"></i>
                                    <small>{item.label}</small>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Argumentação */}
                          <div className="mb-3 p-3 bg-light rounded">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="font-weight-bold">
                                <i className="fas fa-comments text-warning mr-1"></i>
                                Argumentação
                              </span>
                              <span className="badge badge-pill" style={{ backgroundColor: competenciaColors['argumentacao'], color: '#fff' }}>
                                {redacao.argumentacao || 0}/4
                              </span>
                            </div>
                            {getSelectedSubcompetencias('argumentacao').length > 0 && (
                              <div className="mt-2">
                                {getSelectedSubcompetencias('argumentacao').map(item => (
                                  <div key={item.code} className="d-flex align-items-start mb-1">
                                    <i className="fas fa-check-circle text-success mr-2" aria-hidden="true"></i>
                                    <small>{item.label}</small>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Estruturação do Período e Coesão */}
                          <div className="mb-3 p-3 bg-light rounded">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="font-weight-bold">
                                <i className="fas fa-link text-info mr-1"></i>
                                Estruturação do Período e Coesão
                              </span>
                              <span className="badge badge-pill" style={{ backgroundColor: competenciaColors['periodo_coesao'], color: '#fff' }}>
                                {redacao.periodo_coesao || 0}/4
                              </span>
                            </div>
                            {getSelectedSubcompetencias('periodo_coesao').length > 0 && (
                              <div className="mt-2">
                                {getSelectedSubcompetencias('periodo_coesao').map(item => (
                                  <div key={item.code} className="d-flex align-items-start mb-1">
                                    <i className="fas fa-check-circle text-success mr-2" aria-hidden="true"></i>
                                    <small>{item.label}</small>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Modalidade */}
                          <div className="mb-3 p-3 bg-light rounded">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="font-weight-bold">
                                <i className="fas fa-spell-check text-danger mr-1"></i>
                                Modalidade
                              </span>
                              <span className="badge badge-pill" style={{ backgroundColor: competenciaColors['modalidade'], color: '#fff' }}>
                                {redacao.modalidade || 0}/4
                              </span>
                            </div>
                            {getSelectedSubcompetencias('modalidade').length > 0 && (
                              <div className="mt-2">
                                {getSelectedSubcompetencias('modalidade').map(item => (
                                  <div key={item.code} className="d-flex align-items-start mb-1">
                                    <i className="fas fa-check-circle text-success mr-2" aria-hidden="true"></i>
                                    <small>{item.label}</small>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Nota Total */}
                          <div className="alert alert-success mb-4" style={{ flex: '0 0 auto' }}>
                            <h6 className="mb-1">
                              <i className="fas fa-trophy mr-1"></i>
                              Nota Final
                            </h6>
                            <h3 className="mb-0 text-success">
                              {Math.round(
                                Number(redacao.adequacao_tema || 0) +
                                Number(redacao.tipo_texto || 0) +
                                Number(redacao.argumentacao || 0) +
                                Number(redacao.periodo_coesao || 0) +
                                Number(redacao.modalidade || 0)
                              )}/20
                            </h3>
                          </div>

                        {/* Marcações do Professor */}
                        <div className="mb-4 d-flex flex-column" style={{ maxHeight: sectionMaxHeight, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', flex: '0 0 auto' }}>
                          <h6 className="mb-3">
                            <i className="fas fa-highlighter text-warning mr-2"></i>
                            Marcações do Professor
                          </h6>
                          {marcacoes.length > 0 ? (
                            <div>
                              {marcacoes.map((marcacao, index) => (
                                <div
                                  key={index}
                                  className="mb-2 p-2 border rounded marcacao-item"
                                  style={{
                                    backgroundColor: hexToRgba(getCompetenciaColor(resolveCompetencia(marcacao)), 0.15),
                                    borderColor: hexToRgba(getCompetenciaColor(resolveCompetencia(marcacao)), 0.35)
                                  }}
                                >
                                  <div className="d-flex align-items-center mb-1">
                                    <span
                                      className="badge mr-2"
                                      style={{ backgroundColor: getCompetenciaColor(resolveCompetencia(marcacao)), color: '#fff' }}
                                    >
                                      {getCompetenciaLabel(resolveCompetencia(marcacao))}
                                    </span>
                                  </div>
                                  {(marcacao.content?.image || marcacao.image || marcacao.image_url) && (
                                    <div className="mb-2">
                                      <img 
                                        src={marcacao.content?.image || marcacao.image || marcacao.image_url} 
                                        alt="Marcação" 
                                        style={{ 
                                          maxWidth: '100%', 
                                          height: 'auto', 
                                          maxHeight: '100px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px'
                                        }}
                                      />
                                    </div>
                                  )}
                                  {(marcacao.content?.text || marcacao.comment) && (
                                    <small className="text-dark d-block">
                                      <i className="fas fa-comment mr-1"></i>
                                      {marcacao.content?.text || marcacao.comment}
                                    </small>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center text-muted py-3">
                              <i className="fas fa-info-circle" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                              <p className="mt-2 mb-0">Nenhuma marcação encontrada</p>
                              <small>O professor ainda não fez marcações nesta redação</small>
                            </div>
                          )}
                        </div>

                        {/* Botão de Voltar */}
                        <div className="d-grid">
                          <Button
                            variant="outline-secondary"
                            onClick={() => history.goBack()}
                          >
                            <i className="fas fa-arrow-left mr-1"></i>
                            Voltar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </S.MainContent>
            </Col>
          </Row>
        </Container>
      </S.MainWrapper>
    </Template>
  );
}

export default VisualizarCorrecaoUERJ;